import * as React from 'react';

import styled from '@emotion/styled';

import { Skeleton, Typography, VisuallyHidden } from '@coursera/cds-core';

import CatastrophicErrorNotification from 'bundles/admin-shared/components/CatastrophicErrorNotification';
import EmptyResultsRow from 'bundles/admin-shared/components/EmptyResultsRow';
import { Table as OriginalStyledTable, Td, Th, Thead, Tr } from 'bundles/admin-shared/components/styledTableComponents';

import _t from 'i18n!nls/staff-management';

import AdminUserRolesTableBody from './AdminUserRolesTableBody';
import { useAdminUserRolesData } from './StaffPermissionsData/AdminUserRolesData';

const Table = styled(OriginalStyledTable)`
  th,
  td {
    width: 60%;

    &:first-of-type,
    &:last-of-type {
      width: 20%;
    }
  }
`;

const NO_OF_COLUMNS = 3;
const NO_OF_SKELETONS = 3;

const AdminUserRolesTable = () => {
  const { adminUserRoles, adminUserRolesGql } = useAdminUserRolesData();

  if (!adminUserRolesGql) {
    return null;
  }

  const { loading, error, data } = adminUserRolesGql;

  const adminUserRoleDataIsLoading = loading && !data?.AdminUserRoles?.queryByUser?.edges;

  let catastrophicErrorMessage = _t('Could not fetch user roles. Please refresh the page and try again.');
  if (error?.graphQLErrors[0]?.extensions?.errorType === 'PERMISSION_DENIED') {
    catastrophicErrorMessage = _t('Access denied. Power users may check their access logs to enable this view.');
  }

  return (
    <>
      <Table>
        <Thead>
          <Tr>
            <Th scope="col">{_t('Content')}</Th>
            <Th scope="col">{_t('Name')}</Th>
            <Th scope="col">{_t('Role')}</Th>
          </Tr>
        </Thead>
        {adminUserRoleDataIsLoading && (
          <>
            <VisuallyHidden role="status" aria-live="polite" component="span" className="rc-A11yScreenReaderOnly">
              {_t('Loading')}
            </VisuallyHidden>
            {[...Array(NO_OF_SKELETONS)].map((_, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <Tr key={`key-${idx}`}>
                <Td colSpan={NO_OF_COLUMNS}>
                  <Typography variant="inherit">
                    <Skeleton variant="rect" width="100%" />
                  </Typography>
                </Td>
              </Tr>
            ))}
          </>
        )}
        {!adminUserRoleDataIsLoading && !error && <AdminUserRolesTableBody adminUserRoles={adminUserRoles} />}
        {!adminUserRoleDataIsLoading && !error && adminUserRoles.length === 0 && (
          <EmptyResultsRow colSpan={3}>{_t('No result')}</EmptyResultsRow>
        )}
      </Table>
      {error && <CatastrophicErrorNotification message={catastrophicErrorMessage} />}
    </>
  );
};

export default AdminUserRolesTable;
